import { Link } from 'react-router-dom';
import { ExternalLink, Ticket } from 'lucide-react';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { ticketPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

/** Layout pieces shared by Device Orbit and Person Orbit. */

export function Panel({ title, icon: Icon, actions, children, muted }: { title: string; icon?: typeof Ticket; actions?: React.ReactNode; children: React.ReactNode; muted?: boolean }) {
  return (
    <section className={cn('overflow-hidden rounded-lg border bg-card shadow-sm', muted ? 'border-dashed border-border' : 'border-border')}>
      <header className="flex items-center justify-between gap-2 border-b border-divider px-4 py-2.5">
        <h2 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {Icon && <Icon className="h-3.5 w-3.5" />}{title}
        </h2>
        {actions && <div className="flex items-center gap-2 text-xs">{actions}</div>}
      </header>
      {children}
    </section>
  );
}

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-t border-divider px-4 py-1.5 first:border-t-0">
      <span className="shrink-0 pt-px text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="min-w-0 break-words text-right text-[13px] text-foreground">{children ?? <span className="text-muted-foreground">—</span>}</div>
    </div>
  );
}

export type Tone = 'good' | 'warn' | 'bad' | 'muted';
const TONE: Record<Tone, string> = {
  good: 'bg-success-soft text-success',
  warn: 'bg-warning-soft text-warning',
  bad: 'bg-destructive/10 text-destructive',
  muted: 'bg-muted text-muted-foreground',
};
export function Pill({ tone, children, title }: { tone: Tone; children: React.ReactNode; title?: string }) {
  return <span title={title} className={cn('inline-flex items-center rounded-full px-1.5 py-0.5 text-[10.5px] font-semibold', TONE[tone])}>{children}</span>;
}

export function ExtLink({ href, children }: { href: string; children: React.ReactNode }) {
  return <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground hover:underline">{children}<ExternalLink className="h-3 w-3" /></a>;
}

export function TicketList({ items, error, empty }: { items: { id: number; ticketNumber: string; title: string; statusLabel: string; createDate: string; companyName?: string }[]; error: string | null; empty: string }) {
  if (error) return <div className="px-4 py-4 text-xs text-destructive">Couldn't load tickets: {error}</div>;
  if (!items.length) return <div className="px-4 py-4 text-xs text-muted-foreground">{empty}</div>;
  return (
    <ul className="max-h-96 divide-y divide-divider overflow-y-auto">
      {items.map((t) => (
        <li key={t.id}>
          <Link to={ticketPath(t.id)} className="block px-4 py-2 hover:bg-muted/40">
            <div className="truncate text-sm text-foreground">{t.title}</div>
            <div className="text-[11px] text-muted-foreground">{[t.ticketNumber, t.statusLabel, t.companyName, fmtRelative(t.createDate)].filter(Boolean).join(' · ')}</div>
          </Link>
        </li>
      ))}
    </ul>
  );
}
